import { computed, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { FileViewerFile } from '@cognitoforms/file-viewer/src/file-viewer.types';

export type FileViewerGeneration = Pick<FileViewerFile, 'downloadUrl' | 'viewerUrl'> & Partial<Pick<FileViewerFile, 'fileSize'>>;

export type FileViewerFileGenerator = (file: FileViewerFile) => Promise<FileViewerGeneration>;

export type FileViewerGenerationStatus = 'idle' | 'generating' | 'ready' | 'failed';

export const GENERATE_FILE_VIEWER_FILE = 'generateFileViewerFile';

export function useFileViewerGeneration(currentFile: Ref<FileViewerFile | null>, generate: FileViewerFileGenerator | null) {
	const status = ref<FileViewerGenerationStatus>('idle');
	const generated = ref<FileViewerFile | null>(null);
	const results = new WeakMap<FileViewerFile, FileViewerFile>();
	let requestId = 0;

	async function run(file: FileViewerFile): Promise<void> {
		const id = ++requestId;
		status.value = 'generating';
		generated.value = null;

		try {
			const result = await generate!(file);
			if (id !== requestId)
				return;

			const resolved: FileViewerFile = {
				...file,
				downloadUrl: result.downloadUrl,
				viewerUrl: result.viewerUrl,
				fileSize: result.fileSize ?? file.fileSize,
				Size: result.fileSize ?? file.Size,
				requiresGeneration: false
			};

			results.set(file, resolved);
			generated.value = resolved;
			status.value = 'ready';
		}
		catch {
			if (id === requestId)
				status.value = 'failed';
		}
	}

	watch(currentFile, file => {
		if (!file || !file.requiresGeneration || !generate) {
			requestId++;
			status.value = 'idle';
			generated.value = null;
			return;
		}

		const existing = results.get(file);
		if (existing) {
			requestId++;
			generated.value = existing;
			status.value = 'ready';
			return;
		}

		void run(file);
	}, { immediate: true });

	const file = computed<FileViewerFile | null>(() => (status.value === 'ready' ? generated.value : currentFile.value));

	function retry(): void {
		if (currentFile.value?.requiresGeneration && generate)
			void run(currentFile.value);
	}

	return { file, status, retry };
}
